import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { PostSummary, ProjectSummary } from '@/types'

import { getFeaturedProjects, getHomePosts } from '@/lib/content'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { FinalCta } from '@/components/kit/final-cta'
import { AskUsAnything } from '@/components/home/ask-us-anything'
import { BeforeAfter } from '@/components/home/before-after'
import { BehindTheScenes } from '@/components/home/behind-the-scenes'
import { CaseCarousel } from '@/components/home/case-carousel'
import { ContentWall } from '@/components/home/content-wall'
import { CultureTicker } from '@/components/home/culture-ticker'
import { ForecastBoard } from '@/components/home/forecast-board'
import { GroupChat } from '@/components/home/group-chat'
import { Hero } from '@/components/home/hero'
import { HotTakeGenerator } from '@/components/home/hot-take-generator'
import { HunchToHit } from '@/components/home/hunch-to-hit'
import { JournalSpread } from '@/components/home/journal-spread'
import { Manifesto } from '@/components/home/manifesto'
import { MistakesFixed } from '@/components/home/mistakes-fixed'
import { PlatformPlayground } from '@/components/home/platform-playground'
import { PostTimeMachine } from '@/components/home/post-time-machine'
import { Scoreboard } from '@/components/home/scoreboard'
import { StickerSheet } from '@/components/home/sticker-sheet'
import { WindMap } from '@/components/home/wind-map'

interface HomeProps {
    projects: Array<ProjectSummary>
    posts: Array<PostSummary>
}

const Home: FC<HomeProps> = ({ projects, posts }): ReactNode => {
    return (
        <Layout>
            <Seo description="Updraft is a social media studio for brands that want to be talked about. We forecast what is about to trend, launch content into the feed and read the room afterwards. Catch the lift." />

            <Hero />
            <CultureTicker />
            <Manifesto />
            <ForecastBoard />
            <HunchToHit />
            <ContentWall />
            <PlatformPlayground />
            <PostTimeMachine />
            <StickerSheet />
            <Scoreboard />
            <BeforeAfter />
            <MistakesFixed />
            <WindMap />
            <CaseCarousel projects={projects} />
            <GroupChat />
            <HotTakeGenerator />
            <BehindTheScenes />
            <JournalSpread posts={posts} />
            <AskUsAnything />
            <FinalCta />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    try {
        const [projects, posts] = await Promise.all([getFeaturedProjects(4), getHomePosts(5)])

        return { props: { projects, posts }, revalidate: 60 }
    }
    catch (error) {
        console.error('Front page content could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { projects: [], posts: [] }, revalidate: 30 }
    }
}

export default Home
